
import { FormatQuoteRounded } from "@mui/icons-material";
import { Avatar, Box, Stack, Typography } from "@mui/material";

import type { WidgetProps } from "./types";

// Customer testimonial cards. v0.5: stack of cards in a 2-up grid (1-up on
// mobile). Each card opens with a large quote glyph, the quote itself,
// then the author + company attribution row.
export function CustomerTestimonials({ widget }: WidgetProps<"customer_testimonials">) {
    const { title, description, testimonials } = widget.props;

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: description ? 0.5 : 2 }}>
                {title}
            </Typography>
            {description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, maxWidth: 720 }}
                >
                    {description}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                    gap: 2.5,
                }}
            >
                {testimonials.map((t, idx) => (
                    <TestimonialCard key={`${t.companyName}-${idx}`} testimonial={t} />
                ))}
            </Box>
        </Box>
    );
}

type Testimonial = WidgetProps<"customer_testimonials">["widget"]["props"]["testimonials"][number];

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
    const initials = testimonial.authorName
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0))
        .join("");

    return (
        <Stack
            justifyContent="space-between"
            spacing={3}
            sx={{
                p: { xs: 3, md: 3.5 },
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "background.paper",
                position: "relative",
                overflow: "hidden",
                minHeight: 220,
            }}
        >
            <Box>
                <FormatQuoteRounded
                    sx={{
                        fontSize: 40,
                        color: "secondary.main",
                        opacity: 0.85,
                        mb: 1,
                        ml: -0.5,
                    }}
                />
                <Typography
                    variant="body1"
                    sx={{
                        color: "text.primary",
                        fontSize: { xs: "1rem", md: "1.0625rem" },
                        lineHeight: 1.55,
                        fontStyle: "italic",
                    }}
                >
                    &ldquo;{testimonial.quote}&rdquo;
                </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                    src={testimonial.authorPhotoUrl}
                    alt={testimonial.authorName}
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "primary.main",
                        fontSize: "1rem",
                        fontWeight: 600,
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {testimonial.authorName}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.4 }}
                    >
                        {testimonial.authorRole
                            ? `${testimonial.authorRole}, ${testimonial.companyName}`
                            : testimonial.companyName}
                    </Typography>
                </Box>
                {testimonial.companyLogoUrl && (
                    <Box
                        component="img"
                        src={testimonial.companyLogoUrl}
                        alt={testimonial.companyName}
                        sx={{
                            maxHeight: 28,
                            maxWidth: 80,
                            objectFit: "contain",
                            opacity: 0.85,
                        }}
                    />
                )}
            </Stack>
        </Stack>
    );
}
